// src/components/security/SecurityRange.tsx
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Security } from '../../proto/generated/security'
import RangeBar from './RangeBar'
import { getSecurityKey } from '../../util/security'
import { selectOrderBook, selectSnapshot } from '../../selectors/security'
import { RootState } from '../../store.ts'
import { getCandles, getOrderBook } from '../../services/apis/candles'
import { TimeBucket_TimeSeries } from '../../proto/generated/common'
import { updateOrderBooks } from '../../reducer/security.reducer'
import websocketActions from '../../actions/websocket.actions'
import { selectTradingCalendarDataByExchange } from '../../selectors/chart'
import { constructCalendarDataLookup, isTradingHoliday } from '../../util/chart'
import { formatDateToISODate } from '../../util/general'

interface SecurityRangeProps {
    security: Security
    show52WkRange?: boolean
}

interface RangeBarValues {
    ltp: number
    open: number
    dayLow: number
    dayHigh: number
    weekLow?: number
    weekHigh?: number
}

interface CandleData {
    open: number | null
    high: number | null
    low: number | null
    close: number | null
    isFromToday: boolean
}

const SecurityRange: React.FC<SecurityRangeProps> = ({ security, show52WkRange = false }) => {
    const dispatch = useDispatch()
    const { fieldGroups } = useSelector((state: RootState) => state.overviewStore)
    const securityKey = useMemo(() => (security ? getSecurityKey(security.exchange, security.code) : ''), [security])
    const orderBook = useSelector(selectOrderBook(securityKey))
    const snapshot = useSelector(selectSnapshot(securityKey))
    const tradingCalendarData = useSelector(selectTradingCalendarDataByExchange(security.exchange))

    // Candle data state for OHLC fallback
    const [candleData, setCandleData] = useState<CandleData>({
        open: null,
        high: null,
        low: null,
        close: null,
        isFromToday: false
    })

    // Check if orderBook has meaningful data (indices have zero values, also during premarket for all stocks)
    const hasValidOrderBookData = useMemo(() => {
        return (
            orderBook &&
            ((orderBook.lastTradedPrice && orderBook.lastTradedPrice > 0) || (orderBook.open && orderBook.open > 0) || (orderBook.high && orderBook.high > 0) || (orderBook.low && orderBook.low > 0))
        )
    }, [orderBook])

    const calendarDataLookup = useMemo(() => {
        if (!tradingCalendarData) return null
        return constructCalendarDataLookup(tradingCalendarData)
    }, [tradingCalendarData])

    const isHoliday = useMemo(() => {
        if (!calendarDataLookup) return false
        const today = new Date()
        const todayISODate = formatDateToISODate(today, '')
        return isTradingHoliday(todayISODate, calendarDataLookup)
    }, [calendarDataLookup])

    // Extract 52-week range from field groups
    const { weekLow, weekHigh } = useMemo(() => {
        if (!fieldGroups || !Array.isArray(fieldGroups)) return { weekLow: null, weekHigh: null }

        const technicalsGroup = fieldGroups.find((g: any) => g.name === 'Technicals')
        if (!technicalsGroup) return { weekLow: null, weekHigh: null }

        const lowField = technicalsGroup.fields.find((f: any) => f.name === '52 Week Low')
        const highField = technicalsGroup.fields.find((f: any) => f.name === '52 Week High')

        return {
            weekLow: lowField?.value ? parseFloat(lowField.value) : null,
            weekHigh: highField?.value ? parseFloat(highField.value) : null
        }
    }, [fieldGroups])

    // Set up WebSocket subscriptions
    useEffect(() => {
        if (!security) return

        const subscriptions: any = {
            orderBookSubscriptions: [
                {
                    tag: `securityRange_${security.code}`,
                    securities: [{ exchange: security.exchange, code: security.code }],
                    topBidAskOnly: false
                }
            ]
        }

        // Add snapshot subscription only for indices (where orderBook is invalid)
        if (!hasValidOrderBookData) {
            subscriptions.securitySnapshotSubscriptions = [
                {
                    tag: `securityRangeSnapshot_${security.code}`,
                    securities: [{ exchange: security.exchange, code: security.code }]
                }
            ]
        }

        websocketActions.setWebsocketSubscribeList(dispatch, subscriptions)

        // Cleanup subscriptions on unmount
        return () => {
            websocketActions.setWebsocketSubscribeList(dispatch, {
                orderBookSubscriptions: [],
                securitySnapshotSubscriptions: []
            })
        }
    }, [security, dispatch, hasValidOrderBookData])

    // Fetch initial orderBook data
    useEffect(() => {
        if (!orderBook && security) {
            getOrderBook({ security, dispatch })
                .then((result) => {
                    const orderBookData = { ...result }
                    if (!orderBookData.security) {
                        orderBookData.security = { ...security }
                    }
                    dispatch(updateOrderBooks([orderBookData]))
                })
                .catch((error) => {
                    console.error('Error fetching order book:', error)
                })
        }
    }, [orderBook, security, dispatch])

    // Fetch 1D candle data for fallback OHLC
    useEffect(() => {
        let mounted = true

        const fetchDayCandle = async () => {
            if (!security) return

            // Fetch candles when: no orderBook OR holiday OR orderBook invalid (indices)
            if (!orderBook || isHoliday || !hasValidOrderBookData) {
                const referenceDate = new Date()
                referenceDate.setHours(23, 59, 59)

                try {
                    const resp = await getCandles({
                        body: {
                            security: { code: security.code, exchange: security.exchange },
                            timeBucket: { timeSeries: TimeBucket_TimeSeries.DAY, value: 1 },
                            referenceTime: referenceDate.getTime(),
                            forwardCandleCount: 0,
                            backwardCandleCount: 1,
                            dontGiveExtraCandles: true,
                            dontApplyCorporateActionAdjustments: false
                        }
                    })

                    const candle = resp?.backwardCandles?.[0]
                    if (mounted && candle) {
                        const multiplier = security.priceMultiplier ?? 100

                        // Check if candle is from today (prevents pre-market stale data)
                        const candleDate = new Date(candle.startTimestamp || 0)
                        const today = new Date()
                        const isCandleFromToday = candleDate.toDateString() === today.toDateString()

                        setCandleData({
                            open: (candle.open ?? 0) / multiplier,
                            high: (candle.high ?? 0) / multiplier,
                            low: (candle.low ?? 0) / multiplier,
                            close: (candle.close ?? 0) / multiplier,
                            isFromToday: isCandleFromToday
                        })
                    }
                } catch (error) {
                    console.error('Error fetching daily candle:', error)
                }
            }
        }

        fetchDayCandle()
        return () => {
            mounted = false
        }
    }, [security, orderBook, isHoliday, hasValidOrderBookData])

    // Calculate range bar values with priority logic
    const values = useMemo<RangeBarValues | null>(() => {
        if (!security) return null

        const multiplier = security.priceMultiplier ?? 100

        // Case 1: Use orderBook data for stocks (when orderBook has valid data)
        if (hasValidOrderBookData && !isHoliday) {
            const ltp = orderBook.lastTradedPrice ? orderBook.lastTradedPrice / multiplier : orderBook.close ? orderBook.close / multiplier : 0

            return {
                ltp,
                open: (orderBook.open ?? 0) / multiplier,
                dayLow: (orderBook.low ?? 0) / multiplier,
                dayHigh: (orderBook.high ?? 0) / multiplier,
                weekLow: weekLow ?? undefined,
                weekHigh: weekHigh ?? undefined
            }
        }

        // Case 2: For indices - use snapshot LTP + today's candle OHLC (prevents pre-market stale data)
        if (!hasValidOrderBookData && !isHoliday && snapshot && candleData.close !== null && candleData.isFromToday) {
            const ltp = snapshot.lastTradedPrice ? snapshot.lastTradedPrice / multiplier : 0

            return {
                ltp, // Live LTP from snapshot
                open: candleData.open ?? 0,
                dayLow: candleData.low ?? 0,
                dayHigh: candleData.high ?? 0,
                weekLow: weekLow ?? undefined,
                weekHigh: weekHigh ?? undefined
            }
        }

        // Case 3: Same-day candle fallback (when no snapshot available)
        if (candleData.close !== null && candleData.isFromToday && !isHoliday) {
            return {
                ltp: candleData.close,
                open: candleData.open ?? 0,
                dayLow: candleData.low ?? 0,
                dayHigh: candleData.high ?? 0,
                weekLow: weekLow ?? undefined,
                weekHigh: weekHigh ?? undefined
            }
        }

        // Case 4: Holiday case - use orderBook close + candle OHLC
        if (orderBook && isHoliday && candleData.close !== null) {
            const ltp = orderBook.close ? orderBook.close / multiplier : candleData.close

            return {
                ltp,
                open: candleData.open ?? 0,
                dayLow: candleData.low ?? 0,
                dayHigh: candleData.high ?? 0,
                weekLow: weekLow ?? undefined,
                weekHigh: weekHigh ?? undefined
            }
        }

        // Case 5: Final fallback - use candle data only (holidays, service restarts)
        if (candleData.close !== null && candleData.isFromToday) {
            return {
                ltp: candleData.close,
                open: candleData.open ?? 0,
                dayLow: candleData.low ?? 0,
                dayHigh: candleData.high ?? 0,
                weekLow: weekLow ?? undefined,
                weekHigh: weekHigh ?? undefined
            }
        }

        return null
    }, [security, orderBook, candleData, weekLow, weekHigh, isHoliday, snapshot, hasValidOrderBookData])

    if (!values) return null

    const has52WeekData = values.weekLow != null && values.weekHigh != null
    const shouldShow52WkRange = (show52WkRange === true) && has52WeekData

    return <RangeBar ltp={values.ltp} open={values.open} dayLow={values.dayLow} dayHigh={values.dayHigh} weekLow={values.weekLow} weekHigh={values.weekHigh} show52WkRange={shouldShow52WkRange} />
}

export default SecurityRange
